import React, { useEffect, useLayoutEffect, useState } from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import SelectMui, { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';

type SelectOption = { value: string | undefined; label: string | undefined };

interface SelectProps {
  name: string;
  options: SelectOption[];
  onChange: (option: SelectOption) => void;
  label?: string;
  defaultOption?: SelectOption;
  option?: SelectOption;
  withEmptyOption?: boolean;
  autoWidth?: boolean;
  isControlled?: boolean;
  disabled?: boolean;
  isLocalized?: boolean;
}

export const Select = React.forwardRef<any, SelectProps>(
  (
    {
      name,
      options,
      onChange,
      label,
      defaultOption,
      option,
      withEmptyOption = false,
      autoWidth = true,
      isControlled = false,
      disabled = false,
      isLocalized = true,
    }: SelectProps,
    ref
  ) => {
    const [selectedOption, setSelectedOption] = useState<
      SelectOption | undefined
    >(defaultOption);

    const { t } = useTranslation();

    const handleChange = (event: SelectChangeEvent) => {
      if (!isControlled) {
        setSelectedOption({
          value: event.target.value,
          label: event.target.name,
        });
      }

      onChange({ value: event.target.value, label: event.target.name });
    };

    const renderItems = () => {
      let newOptions: SelectOption[] = options;

      if (withEmptyOption) {
        newOptions = [{ value: '', label: 'None' } as SelectOption].concat(
          options
        );
      }

      return newOptions.map((o) => {
        return (
          <MenuItem key={o.value} value={o.value} sx={{ minWidth: '150px' }}>
            {isLocalized
              ? t(`selects.labels.${o.label}`, o.label || '')
              : o.label}
          </MenuItem>
        );
      });
    };

    useLayoutEffect(() => {
      const style = document.createElement('style');

      style.innerHTML = `
        .MuiPaper-root ul li {
            font-family: "Tajawal" !important;
        }
    `;

      const head = document.head;

      head.appendChild(style);
    }, []);

    useEffect(() => {
      setSelectedOption(option);
    }, [option]);

    return (
      <div style={{ width: '100%', height: '100%' }}>
        <FormControl
          sx={{
            width: '100%',
            minWidth: '150px',
            height: '100%',
            backgroundColor: 'white',
            '& .Mui-focused': {
              '& fieldset': { borderColor: '#38A7B4 !important' },
            },
            py: '0',
            '& > div': { py: '8px', fontFamily: 'Tajawal !important' },
            '&:hover': {
              '& fieldset': {
                borderColor: '#38A7B4 !important',
              },
            },
          }}
        >
          <InputLabel id={name}>{label}</InputLabel>
          <SelectMui
            ref={ref}
            labelId={name}
            id={`select-${name}`}
            value={selectedOption?.value || ''}
            onChange={handleChange}
            autoWidth={autoWidth}
            label={label}
            sx={{
              minWidth: '150px',
              height: '100%',
              py: '0',
              '& > div': { py: '8px', fontFamily: 'Tajawal !important' },
              '&:hover': {
                '& fieldset': {
                  borderColor: '#38A7B4 !important',
                },
              },
            }}
            disabled={disabled}
          >
            {renderItems()}
          </SelectMui>
        </FormControl>
      </div>
    );
  }
);
